import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';

/**
 * Загрузка данных экрана: три состояния вместо одного.
 *
 * «Пусто», «загрузка» и «ошибка» в рабочем месте — не исключения, а половина
 * рабочего дня, и различать их обязан каждый экран (разд. 14, 15). Отдельная
 * библиотека состояния ради этого не нужна: запросов немного, и все они — чтение
 * списка либо одиночной записи.
 */

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly reload: () => void;
}

export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  options: { readonly intervalMs?: number | undefined } = {},
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(() => setTick((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    loadRef
      .current()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          // Ошибка показывается рядом с действием, а не тостом в углу (разд. 15):
          // экран сам решает, где её разместить, — здесь она только сохраняется.
          setError(
            cause instanceof ApiError
              ? cause
              : new ApiError({ status: 0, code: 'network', title: String(cause) }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    /*
     * Список зависимостей собирается вызывающей стороной: экран сам знает, что
     * должно вызывать перезагрузку. Функция `load` в него не входит намеренно —
     * она пересоздаётся на каждый рендер и вызвала бы бесконечный цикл.
     */
  }, [...deps, tick]);

  useEffect(() => {
    const interval = options.intervalMs;

    if (interval === undefined) {
      return;
    }

    // Периодический опрос — это и есть работа при мёртвом сокете (разд. 16): экран
    // обязан оставаться правдивым, а не замирать вместе с соединением.
    const timer = setInterval(reload, interval);

    return () => clearInterval(timer);
  }, [options.intervalMs, reload]);

  return { data, loading, error, reload };
}
